import { useCallback } from 'react';
import { useRouter } from 'expo-router';

import { useActorRef, useSelector } from '@xstate5/react';

import { useTelemetry } from '@order/Telemetry';

import { cartHandoffMachine } from '../../machine/cart-handoff-machine';
import { useFetchInitialData } from '../useFetchInitialData';
import { useHandoffCart } from '../useHandoffCart';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to handle cart handoff from a URL path parameter.
 * If successful, navigates to the bag screen, other navigates back home.
 * @see {@link https://sweetgreen.atlassian.net/wiki/spaces/TD/pages/7249297413/Agentic+Ordering}
 */
export const useCartHandoffMachine = () => {
  const router = useRouter();

  // ─── Telemetry ─────────────────────────────────────────────────────────────

  const { track } = useTelemetry();

  const trackHandoffStart = useCallback(() => {
    track('app.cart-handoff.start');
  }, [track]);

  const trackActiveBagWarning = useCallback(() => {
    track('app.cart-handoff.active-bag-warning');
  }, [track]);

  const trackConflictsWarning = useCallback(() => {
    track('app.cart-handoff.conflicts-warning');
  }, [track]);

  const trackNothingAvailable = useCallback(() => {
    track('app.cart-handoff.nothing-available');
  }, [track]);

  const trackHandoffSuccess = useCallback(() => {
    track('app.cart-handoff.success');
  }, [track]);

  // ─── Routing ───────────────────────────────────────────────────────────────

  const dismissToHome = useCallback(() => {
    router.dismissTo('/');
  }, [router]);

  const replaceWithBag = useCallback(() => {
    router.replace('/bag');
  }, [router]);

  // ─── Machine ───────────────────────────────────────────────────────────────

  const { fetchInitialData } = useFetchInitialData();
  const { handoffCart } = useHandoffCart();

  const actorRef = useActorRef(
    cartHandoffMachine.provide({
      actors: {
        fetchInitialData,
        handoffCart,
      },
      actions: {
        dismissToHome,
        replaceWithBag,
        trackHandoffStart,
        trackActiveBagWarning,
        trackConflictsWarning,
        trackNothingAvailable,
        trackHandoffSuccess,
      },
    }),
  );

  const conflicts = useSelector(actorRef, (state) => state.context.conflicts);
  const isDismissed = useSelector(actorRef, (state) => state.matches('dismissed'));
  const isInActiveBagWarning = useSelector(actorRef, (state) => state.matches('activeBagWarning'));
  const isInConflictsWarning = useSelector(actorRef, (state) => state.matches('conflictsWarning'));
  const isNothingAvailable = useSelector(actorRef, (state) => state.matches('nothingAvailable'));
  const isHandingOffCart = useSelector(actorRef, (state) => state.matches('cartHandoff') || state.matches('fetchingInitialData')); // prettier-ignore

  const triggerHandoff = useCallback(() => {
    actorRef.send({ type: 'TRIGGER_HANDOFF' });
  }, [actorRef]);

  const dismissHandoff = useCallback(() => {
    track('app.cart-handoff.dismissed');
    actorRef.send({ type: 'DISMISS_HANDOFF' });
  }, [actorRef, track]);

  return {
    conflicts,
    isDismissed,
    isInActiveBagWarning,
    isInConflictsWarning,
    isNothingAvailable,
    isHandingOffCart,
    triggerHandoff,
    dismissHandoff,
  };
};
